import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Mic, Square, Play, RotateCcw, Trophy, Volume2, Lightbulb, Headphones, MessageCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  read_aloud: { label: "Read Aloud", icon: BookOpen, hint: "Read the passage clearly and at a steady pace." },
  listen_repeat: { label: "Listen & Repeat", icon: Headphones, hint: "Play the audio, then repeat the sentence exactly." },
  open_ended: { label: "Open Speaking", icon: MessageCircle, hint: "Answer the question in your own words for ~30 seconds." },
};

export default function Versant() {
  const { data, isLoading } = useQuery({
    queryKey: ["versant-prompts"],
    queryFn: async () => (await api.get("/versant/prompts")).data,
  });
  const { data: histData, refetch } = useQuery({
    queryKey: ["versant-results"],
    queryFn: async () => (await api.get("/versant/results")).data,
  });
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("read_aloud");

  if (isLoading)
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (active) return <Recorder prompt={active} onDone={() => refetch()} onExit={() => setActive(null)} />;

  const prompts = (data?.prompts || []).filter((p) => (p.type || "read_aloud") === tab);
  const results = histData?.results || [];

  return (
    <div className="space-y-6" data-testid="versant-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">AMCAT / Versant</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Voice & Accent</h1>
        <p className="text-muted-foreground mt-1">Practice read-aloud, listen-and-repeat, and open speaking tasks with AI feedback.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 max-w-xl">
          {Object.entries(TYPES).map(([k, v]) => {
            const Icon = v.icon;
            return <TabsTrigger key={k} value={k} data-testid={`versant-tab-${k}`}><Icon className="w-4 h-4 mr-2" /> {v.label}</TabsTrigger>;
          })}
        </TabsList>
      </Tabs>

      <p className="text-sm text-muted-foreground -mt-2">{TYPES[tab].hint}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {prompts.map((p) => {
          const Icon = TYPES[p.type || "read_aloud"].icon;
          return (
            <div key={p.id} className="p-6 rounded-md border border-border bg-card flex flex-col hover:-translate-y-1 transition-transform" data-testid={`versant-prompt-${p.id}`}>
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{p.difficulty}</span>
              <h3 className="font-heading font-bold text-lg mb-2">{p.title}</h3>
              <p className="text-sm text-muted-foreground mb-6 flex-1 italic">{p.type === "open_ended" ? p.text : `"${p.text}"`}</p>
              <Button className="rounded-md gap-2" onClick={() => setActive(p)} data-testid={`practice-${p.id}`}>
                <Mic className="w-4 h-4" /> Practice
              </Button>
            </div>
          );
        })}
      </div>

      {results.length > 0 && (
        <div className="p-6 rounded-md border border-border bg-card">
          <h3 className="font-heading font-bold text-lg mb-4">Your Recent Attempts</h3>
          <div className="space-y-2" data-testid="versant-history">
            {results.slice(0, 8).map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.prompt_title}</p>
                  <p className="text-xs text-muted-foreground font-mono">{(TYPES[r.type] || TYPES.read_aloud).label} · Pron {r.pronunciation} · Fluency {r.fluency}</p>
                </div>
                <span className={`font-mono font-bold ${r.overall >= 75 ? "text-success" : r.overall >= 50 ? "text-chart-4" : "text-destructive"}`}>{r.overall}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Recorder({ prompt, onDone, onExit }) {
  const ptype = prompt.type || "read_aloud";
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [loadingTts, setLoadingTts] = useState(false);
  const [heard, setHeard] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef();
  const ttsAudioRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const playPrompt = async () => {
    setLoadingTts(true);
    try {
      const token = localStorage.getItem("pm_token");
      const res = await fetch(`${API}/versant/tts/${prompt.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      ttsAudioRef.current = a;
      a.play();
      setHeard(true);
    } catch {
      toast.error("Could not load audio. Try again.");
    } finally {
      setLoadingTts(false);
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied. Please allow mic permission.");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const analyze = async () => {
    if (!audioBlob) return;
    setAnalyzing(true);
    const fd = new FormData();
    fd.append("prompt_id", prompt.id);
    fd.append("audio", audioBlob, "recording.webm");
    try {
      const token = localStorage.getItem("pm_token");
      const res = await fetch(`${API}/versant/submit`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("failed");
      const json = await res.json();
      setResult(json.result);
      onDone();
      toast.success("Analysis complete!");
    } catch {
      toast.error("Could not analyze recording. Try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => { setAudioUrl(null); setAudioBlob(null); setResult(null); setSeconds(0); };

  const typeInfo = TYPES[ptype];
  const TypeIcon = typeInfo.icon;
  const canRecord = ptype !== "listen_repeat" || heard;

  return (
    <div className="max-w-2xl mx-auto" data-testid="versant-recorder">
      <button onClick={onExit} className="text-sm text-muted-foreground hover:text-foreground mb-6">← Back</button>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded bg-primary/10 text-primary"><TypeIcon className="w-3.5 h-3.5" /> {typeInfo.label}</span>
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{prompt.difficulty}</span>
      </div>

      {ptype === "listen_repeat" ? (
        <div className="p-8 rounded-md border border-border bg-card mb-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">Listen carefully, then repeat the sentence exactly.</p>
          <Button variant="outline" className="rounded-md gap-2 h-12" onClick={playPrompt} disabled={loadingTts} data-testid="play-prompt-audio">
            {loadingTts ? <Loader2 className="w-5 h-5 animate-spin" /> : <Volume2 className="w-5 h-5" />} {heard ? "Play Again" : "Play Audio"}
          </Button>
          {heard && <p className="text-xs text-success mt-3 font-mono">Audio played — now record your repeat.</p>}
        </div>
      ) : (
        <div className="p-8 rounded-md border border-border bg-card mb-8">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">{ptype === "open_ended" ? "Question" : "Read this aloud"}</p>
          <p className="font-heading text-2xl leading-relaxed">{ptype === "open_ended" ? prompt.text : `"${prompt.text}"`}</p>
        </div>
      )}

      {!result ? (
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            {recording && <div className="absolute inset-0 rounded-full bg-destructive/20 animate-ping" />}
            <button
              onClick={recording ? stop : (audioUrl ? reset : start)}
              disabled={!canRecord}
              data-testid="record-button"
              className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                recording ? "bg-destructive text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              {recording ? <Square className="w-8 h-8" /> : audioUrl ? <RotateCcw className="w-8 h-8" /> : <Mic className="w-9 h-9" />}
            </button>
          </div>
          <p className="font-mono text-lg" data-testid="record-timer">
            {!canRecord ? "Play the audio first" : recording ? `Recording... ${seconds}s` : audioUrl ? "Tap to re-record" : "Tap to start recording"}
          </p>

          {audioUrl && !recording && (
            <div className="w-full space-y-4">
              <audio controls src={audioUrl} className="w-full" data-testid="audio-playback" />
              <Button className="w-full rounded-md gap-2 h-11" onClick={analyze} disabled={analyzing} data-testid="analyze-button">
                {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing speech...</> : <><Play className="w-4 h-4" /> Get AI Feedback</>}
              </Button>
              {analyzing && <div className="h-1 rounded-full tracing-beam" />}
            </div>
          )}
        </div>
      ) : (
        <div className="fade-in-up" data-testid="versant-result">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl font-black">{result.overall}<span className="text-lg text-muted-foreground">/100</span></h2>
            <p className="text-muted-foreground">Overall score</p>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <ScoreBox label="Pronunciation" value={result.pronunciation} testid="score-pronunciation" />
            <ScoreBox label="Fluency" value={result.fluency} testid="score-fluency" />
            <ScoreBox label={ptype === "open_ended" ? "Relevance" : "Accuracy"} value={result.accuracy} testid="score-accuracy" />
          </div>
          <div className="p-5 rounded-md border border-border bg-card mb-4">
            <p className="text-sm font-semibold mb-2">AI Feedback</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{result.feedback}</p>
          </div>
          {result.tips?.length > 0 && (
            <div className="p-5 rounded-md border border-border bg-card mb-4">
              <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-primary" /> Tips</p>
              <ul className="space-y-2">
                {result.tips.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">•</span>{t}</li>)}
              </ul>
            </div>
          )}
          {result.transcript && (
            <div className="p-4 rounded-md border border-border bg-secondary/40 mb-6">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">What we heard</p>
              <p className="text-sm italic">"{result.transcript}"</p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-md gap-2" onClick={reset} data-testid="versant-retry"><RotateCcw className="w-4 h-4" /> Try Again</Button>
            <Button className="rounded-md" onClick={onExit} data-testid="versant-done">Done</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBox({ label, value, testid }) {
  return (
    <div className="p-4 rounded-md border border-border bg-card text-center" data-testid={testid}>
      <p className="font-mono text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
